import { useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import FlatPickr from "react-flatpickr";
import { BarChart3, Download, RotateCcw, Search, Users } from "lucide-react";
import { getAllVenueBySlug, getReport, getTicketTypeBySlug, downloadTicketByBookingNo } from "../../../api";
import ExportToExcel from "../../../components/ExportToExcel/ExportToExcel";
import FormattedDate from "../../../components/common/DateFormat/FormattedDate";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import TablesV2 from "../../../components/common/Tables/TablesV2";
import CommonSelect from "../../../components/common/Select/CommonSelect";
import { handleError } from "../../../utils/errorHandler";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";
import { PulseLoader } from "react-spinners";

/**
 * Attendee Report — paid bookings for the current event.
 * Filters and export use the same v3 shell as Contacts / Enquiries.
 */
const AttendeeReport = () => {
  const { isNightMode } = useNightMode();
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [email, setEmail] = useState("");
  const [phno, setPhno] = useState("");
  const [fullAttendeeList, setFullAttendeeList] = useState<any[]>([]);
  const [attendee, setAttendee] = useState<any[]>([]);
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [downloadingTicket, setDownloadingTicket] = useState(false);
  const [eventVenues, setEventVenues] = useState<any[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<string>("0");

  const getEventVenue = async (slugId: string) => {
    try {
      const resp = await getAllVenueBySlug(slugId);
      const venueOptions = (resp || []).map((item: any) => ({
        value: String(item.venueId),
        label: item.venueName,
      }));
      setEventVenues([{ value: "0", label: "All Venues" }, ...venueOptions]);
    } catch (error) {
      handleError(error);
    }
  };

  const generateExportData = (data: any[]) =>
    data.map((item: any) => ({
      firstName: item?.Contact?.firstName || "",
      email: item?.Contact?.email || "",
      phoneNo: item?.Contact?.phoneNo || "",
      bookingNumber: item?.bookingNo || "",
      ticketType: item?.TicketType?.ticketType || "",
      ticketPrice: item?.TicketType?.price || "",
      totalTicketCount: item?.totalTicketCount || "",
      totalTicketAmount: item?.totalTicketAmount || "",
      grandTotal: item?.grandTotal || "",
      bookingDate: item?.createdAt || "",
      venueName: item?.Venue?.venueName || "",
    }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const eventData = localStorage.getItem("eventData");
        if (!eventData) return;

        const parsed = JSON.parse(eventData);
        const eventSlug = parsed.eventId;
        const slugId = parsed.slugId;

        const [reportResponse] = await Promise.all([
          getReport(eventSlug),
          getTicketTypeBySlug(slugId),
          getEventVenue(slugId),
        ]);

        if (reportResponse) {
          // Primary attendee bookings that completed payment
          const filtered = reportResponse.filter(
            (item: any) => item.paymentStatus === "paid" && item.bookedBy === "0"
          );
          setFullAttendeeList(filtered);
          setAttendee(filtered);
        }
      } catch (error) {
        console.error("Error fetching attendee report:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const onSelectedVenue = async (venueId: string) => {
    setIsLoading(true);
    try {
      const eventData = localStorage.getItem("eventData");
      if (!eventData) return;
      const eventSlug = JSON.parse(eventData).eventId;
      const reportResponse = await getReport(eventSlug);
      const filtered =
        venueId !== "0"
          ? reportResponse.filter(
              (item: any) => item.paymentStatus === "paid" && String(item.venueId) === venueId
            )
          : reportResponse.filter((item: any) => item.paymentStatus === "paid");
      setFullAttendeeList(filtered);
      setAttendee(filtered);
      setSelectedVenue(venueId);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const filtered = fullAttendeeList.filter((row: any) => {
      const first = row?.bookingObject?.primaryContactDetails?.firstName || row?.Contact?.firstName || "";
      const last = row?.bookingObject?.primaryContactDetails?.lastName || row?.Contact?.lastName || "";
      const name = `${first} ${last}`.trim().toLowerCase();
      const emailAddr = (
        row?.bookingObject?.primaryContactDetails?.email ||
        row?.Contact?.email ||
        ""
      ).toLowerCase();
      const phone = (
        row?.bookingObject?.primaryContactDetails?.phoneNo ||
        row?.Contact?.phoneNo ||
        ""
      ).toLowerCase();
      const bookingDate = new Date(row?.createdAt);

      if (fromDate && bookingDate < new Date(fromDate)) return false;
      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        if (bookingDate > end) return false;
      }

      const qName = search.trim().toLowerCase();
      const qEmail = email.trim().toLowerCase();
      const qPhone = phno.trim().replace(/^0+/, "").toLowerCase();

      return (
        (!qName || name.includes(qName)) &&
        (!qEmail || emailAddr.includes(qEmail)) &&
        (!qPhone || phone.replace(/^0+/, "").includes(qPhone))
      );
    });
    setAttendee(filtered);
  }, [search, email, phno, fullAttendeeList, fromDate, toDate]);

  const clearFilters = () => {
    setSearch("");
    setEmail("");
    setPhno("");
    setFromDate(undefined);
    setToDate(undefined);
    if (selectedVenue !== "0") {
      onSelectedVenue("0");
    }
  };

  const hasActiveFilters =
    Boolean(search || email || phno || fromDate || toDate) || selectedVenue !== "0";

  const handleDownloadTicket = async (row: any) => {
    try {
      setDownloadingTicket(true);
      const bookingNo = row?.primaryBookingNo || row?.bookingNo;
      if (!bookingNo) throw new Error("Booking number not found");
      const response = await downloadTicketByBookingNo(bookingNo);
      if (!response) throw new Error("Failed to download ticket");
      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      handleError(err);
    } finally {
      setDownloadingTicket(false);
    }
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "name",
        header: "NAME",
        accessorFn: (row) =>
          `${row?.Contact?.firstName || ""} ${row?.Contact?.lastName || ""}`.trim() ||
          row?.Contact?.email ||
          "Guest",
        cell: ({ row }) => {
          const item = row.original;
          const name =
            `${item?.Contact?.firstName || ""} ${item?.Contact?.lastName || ""}`.trim() || "Guest";
          return (
            <div className="d-flex flex-column gap-1">
              <span className="fw-bold contact-name-text fs-13px">{name}</span>
              <span className="font-mono contact-sub-text fs-11px">{item?.Contact?.email || "—"}</span>
              <span className="font-mono contact-sub-text fs-11px">{item?.Contact?.phoneNo || "—"}</span>
              {item?.Venue?.venueName && (
                <span className="contact-sub-text fs-11px">{item.Venue.venueName}</span>
              )}
            </div>
          );
        },
      },
      {
        id: "ticketType",
        header: "TICKET TYPE",
        accessorFn: (row) => row?.TicketType?.ticketType || "N/A",
        cell: ({ row }) => (
          <span className="contact-sub-text fs-12px">{row.original?.TicketType?.ticketType || "N/A"}</span>
        ),
      },
      {
        accessorKey: "totalTicketCount",
        header: "TICKETS",
        cell: ({ row }) => (
          <span className="font-mono contact-name-text fs-12px">{row.original?.totalTicketCount ?? 0}</span>
        ),
      },
      {
        accessorKey: "totalTicketAmount",
        header: "AMOUNT",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">{row.original?.totalTicketAmount ?? 0}</span>
        ),
      },
      {
        accessorKey: "grandTotal",
        header: "GRAND TOTAL",
        cell: ({ row }) => (
          <span className="font-mono contact-name-text fs-12px">{row.original?.grandTotal ?? 0}</span>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "BOOKED",
        cell: ({ row }) =>
          row.original?.createdAt ? (
            <span className="font-mono contact-sub-text fs-12px">
              <FormattedDate dateString={row.original.createdAt} />
            </span>
          ) : (
            <span className="contact-sub-text fs-12px">N/A</span>
          ),
      },
      {
        id: "action",
        header: "ACTION",
        enableSorting: false,
        cell: ({ row }) =>
          row.original?.bookingStatus === "confirmed" ? (
            <button
              type="button"
              className="contact-action-btn"
              onClick={() => handleDownloadTicket(row.original)}
              disabled={downloadingTicket}
            >
              <Download size={14} /> Ticket
            </button>
          ) : (
            <span className="contact-sub-text fs-12px">—</span>
          ),
      },
    ],
    [downloadingTicket]
  );

  return (
    <div className={`contact-list-container ${isNightMode ? "text-white" : "text-dark"}`}>
      <Background />

      <PageHeader
        title="Attendee Report"
        subtitle="Search paid attendees by name, email, phone, or booking date, then export the filtered list."
        icon={BarChart3}
        isLoading={isLoading}
        actions={
          <ExportToExcel
            data={generateExportData(attendee)}
            fileName="Attendee_Report"
            buttonText="Export (.CSV)"
          />
        }
      />

      <div className="contact-stats-grid" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">MATCHING ATTENDEES</div>
            <div className="contact-stat-value">
              {isLoading ? "..." : attendee.length}{" "}
              <span className="fs-14px fw-normal contact-sub-text">rows</span>
            </div>
          </div>
          <div className="contact-stat-icon pink">
            <Users size={22} />
          </div>
        </div>
        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">TOTAL LOADED</div>
            <div className="contact-stat-value">
              {isLoading ? "..." : fullAttendeeList.length}{" "}
              <span className="fs-14px fw-normal contact-sub-text">bookings</span>
            </div>
          </div>
          <div className="contact-stat-icon orange">
            <BarChart3 size={22} />
          </div>
        </div>
      </div>

      <div className="contact-filter-card attendee-report-filters">
        <div className="attendee-filter-grid">
          {eventVenues.length > 1 && (
            <div className="attendee-filter-field">
              <CommonSelect
                options={eventVenues}
                value={selectedVenue}
                onChange={(e) => onSelectedVenue(e.target.value)}
                label="Event Venue"
              />
            </div>
          )}

          <div className="attendee-filter-field position-relative">
            <label className="subuser-stat-label">Name</label>
            <Search className="attendee-filter-icon contact-sub-text" size={16} />
            <input
              type="text"
              className="contact-search-input attendee-filter-input"
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="attendee-filter-field position-relative">
            <label className="subuser-stat-label">Email</label>
            <Search className="attendee-filter-icon contact-sub-text" size={16} />
            <input
              type="text"
              className="contact-search-input attendee-filter-input"
              placeholder="Search by email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="attendee-filter-field position-relative">
            <label className="subuser-stat-label">Phone</label>
            <Search className="attendee-filter-icon contact-sub-text" size={16} />
            <input
              type="text"
              className="contact-search-input attendee-filter-input"
              placeholder="Search by phone"
              value={phno}
              onChange={(e) => setPhno(e.target.value)}
            />
          </div>

          <div className="attendee-filter-field">
            <label className="subuser-stat-label">Start Date</label>
            <FlatPickr
              className="contact-search-input attendee-date-input"
              value={fromDate}
              onChange={([date]) => setFromDate(date)}
              options={{ dateFormat: "Y-m-d" }}
              placeholder="Select start date"
            />
          </div>

          <div className="attendee-filter-field">
            <label className="subuser-stat-label">End Date</label>
            <FlatPickr
              className="contact-search-input attendee-date-input"
              value={toDate}
              onChange={([date]) => setToDate(date)}
              options={{ dateFormat: "Y-m-d" }}
              placeholder="Select end date"
            />
          </div>
        </div>

        {hasActiveFilters && (
          <div className="d-flex justify-content-end mt-3">
            <button type="button" className="contact-action-btn" onClick={clearFilters}>
              <RotateCcw size={14} /> Clear Filters
            </button>
          </div>
        )}
      </div>

      {downloadingTicket && (
        <div className="d-flex align-items-center justify-content-center py-2 gap-2">
          <PulseLoader color="#EE3F7C" size={8} />
          <span className="contact-sub-text fs-12px">Preparing ticket PDF…</span>
        </div>
      )}

      <TablesV2
        data={attendee}
        columns={columns}
        isLoading={isLoading}
        getRowId={(row, idx) => String(row.bookingId || row.bookingNo || `att-${idx}`)}
        loadingMessage="Loading attendee report..."
        emptyMessage="No attendees match your current filters."
        pageSize={25}
      />
    </div>
  );
};

export default AttendeeReport;
